import React, { useMemo, useState, useCallback, useContext } from 'react';
import styled from 'styled-components';
import { Field } from 'react-final-form';
import { useQueryClient } from 'react-query';
import usePublications from '@ubeya/shared/hooks/account/usePublications';
import useAccount from '@ubeya/shared/hooks/account/useAccount';
import {
  Modal as BaseModal,
  Wizard,
  FlexColumn,
  Loader,
  FlexCenter,
  ConfirmationModal
} from '@ubeya/shared-web/components';
import { media } from '@ubeya/shared-web/style';
import { itemsSelected } from '@ubeya/shared-web/utils/checkbox';
import useConfetti from '@ubeya/shared/hooks/useConfetti';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import FeatureGateError from '@ubeya/shared/errors/FeatureGateError';
import { isMobileOnly } from 'react-device-detect';
import { trackEvent } from '@ubeya/shared-web/analytics/amplitude';
import PublishEmployees from './PublishEmployees';
import PublishSettings from './PublishSettings';
import PublishOneItem from './PublishOneItem';

const Modal = styled(BaseModal)`
  ${media.mobile`height: 73vh`};
  max-height: unset;

  > .content {
    overflow: hidden;
    > form {
      overflow: hidden;
    }
  }
`;

const Container = styled(FlexColumn)`
  flex: 1;
  overflow: hidden;
  max-height: 100%;
`;

const LoaderWrapper = styled(FlexCenter)`
  height: 100vh;
`;

const PublishModal = ({
  isShift,
  onClose,
  kind,
  title,
  items,
  showPrompt = false,
  branches,
  onConfirm,
  className,
  addMode = true,
  setNotificationConditions,
  generateDisabledFilters,
  fireConfetti = false,
  params,
  selectedShifts = [],
  isLoadingData = false,
  isCheckAvailabilityNeeded = false
}) => {
  const [search, setSearch] = useState('');
  const { setFire } = useConfetti();
  const queryClient = useQueryClient();
  const { t } = useContext(ConfigContext);

  const [showPromptPublishNotSeen, setShowPromptPublishNotSeen] = useState(false);

  const { accountId } = useAccount();

  const filteredItems = useMemo(() => (addMode ? items.filter(({ publicationId }) => !publicationId) : items), [
    items,
    addMode
  ]);

  const publicationIds = filteredItems.map(({ publicationId }) => publicationId);

  const { isLoading: isLoadingPublications, publicationsMap, updatePublications } = usePublications({
    publicationIds,
    enable: publicationIds.length > 0
  });

  const isLoading = isLoadingPublications || isLoadingData;

  const getInitialCheckedValues = useCallback(
    ({ audience, publicationId }) => {
      const { users } = publicationsMap?.[publicationId] || {};

      if (users && users.length) {
        return { include: users.map(({ employeeId }) => employeeId)?.filter((id) => !!id) };
      }
      return audience ?? { all: true };
    },
    [publicationsMap]
  );

  const initialValues = useMemo(
    () => ({
      checkedValue: { all: !selectedShifts.length, include: selectedShifts },
      items: filteredItems.reduce(
        (
          prev,
          {
            id,
            publicationId,
            positionId,
            shiftDate,
            filters,
            audience,
            locationId,
            title: itemTitle,
            duration,
            comments
          }
        ) => ({
          ...prev,
          [id]: {
            shiftId: id,
            publicationId,
            locationId,
            title: itemTitle,
            duration,
            comments,
            positionId,
            shiftDate,
            filters: publicationsMap[publicationId]?.filters
              ? {
                  fields: publicationsMap[publicationId].filters.fields,
                  /* filter cases of unknown bug that the value is empty array */
                  basic: publicationsMap[publicationId].filters.basic.filter(({ value }) =>
                    Array.isArray(value) ? !!value?.length : true
                  )
                }
              : filters,
            audience: getInitialCheckedValues({ audience, publicationId })
          }
        }),
        {}
      ),
      notification: { publish: 'publishNow', pushNotification: true, email: false }
    }),
    [filteredItems, getInitialCheckedValues, publicationsMap, selectedShifts]
  );

  const filteredInitialValues = useMemo(() => {
    return {
      ...initialValues,
      items: Object.fromEntries(
        Object.entries(initialValues?.items).filter(([, { title: itemTitle, duration, comments }]) =>
          search
            ? [itemTitle, duration, comments].filter(Boolean).join(' ').toLowerCase().includes(search.toLowerCase())
            : true
        )
      )
    };
  }, [initialValues, search]);

  const initialCheckedValues = useMemo(() => {
    return Object.entries(filteredInitialValues?.items).reduce((all, [id, data]) => {
      all[id] = data?.audience?.include;
      return all;
    }, {});
  }, [filteredInitialValues]);

  const checkForNewAudience = useCallback(
    (data) => {
      const newAudience = data.reduce((all, item) => {
        all[item.id] = item?.audience?.include?.filter((value) => !initialCheckedValues[item.id]?.includes(value));
        return all;
      }, {});

      return Object.values(newAudience).some((audience) => audience?.length > 0);
    },
    [initialCheckedValues]
  );

  const formatAudience = (audienceItem = {}) =>
    Object.entries(audienceItem).reduce((all, [key, value]) => {
      if (value) {
        all[key] = value;
      }
      return all;
    }, {});

  const handleSubmit = useCallback(
    async (values) => {
      trackEvent('Check availability - send button clicked', {
        pushNotificationEnabled: values.notification?.pushNotification,
        emailEnabled: values.notification?.email,
        emailSubject: values.notification?.emailSubject,
        hasMessage: !!values.notification?.suffix,
        type: values.notification?.publish,
        kind,
        isShift,
        title
      });

      const submittedItems = Object.entries(values.items).map(([key, value]) => ({
        id: parseInt(key, 10),
        ...value,
        audience: formatAudience(value.audience)
      }));
      const selectedItems = itemsSelected({ value: values.checkedValue, items: submittedItems });
      const updatedPublication = submittedItems.filter(({ publicationId }) => publicationId);

      let data;
      try {
        data = await updatePublications({
          ...values,
          kind,
          items: selectedItems,
          branches
        });
      } catch (e) {
        if (e instanceof FeatureGateError) {
          onClose();
          return;
        }
        throw e;
      }

      const ids = filteredItems.map(({ publicationId }) => publicationId);

      if (data) {
        queryClient.invalidateQueries({
          predicate: ({ queryKey }) =>
            queryKey[0] === 'publications' && queryKey[1] === accountId && (ids || []).includes(queryKey[2])
        });

        if (filteredItems.length === 1) {
          const storeKey = ['publications', accountId, [filteredItems[0]?.publicationId]];
          [...updatedPublication].forEach(({ publicationId }) =>
            queryClient.invalidateQueries(['publications', accountId, publicationId], { exact: true })
          );
          queryClient.invalidateQueries(storeKey);
        }
      }

      await onConfirm?.();
      if (showPrompt) {
        const publishToNotSeenAudience = checkForNewAudience(selectedItems);
        if (publishToNotSeenAudience) {
          return setShowPromptPublishNotSeen(values);
        }
      }
      onClose();
      fireConfetti && setFire(true);
    },
    [
      accountId,
      branches,
      checkForNewAudience,
      filteredItems,
      fireConfetti,
      kind,
      onClose,
      onConfirm,
      queryClient,
      setFire,
      showPrompt,
      updatePublications,
      isShift,
      title
    ]
  );

  return (
    <Modal
      onClose={onClose}
      size={isMobileOnly ? 'large' : 'medium'}
      submit={false}
      showConfirmButton={false}
      title={title}
      closeOnClickOutside={false}
      isPaddingContent={false}
      className={className}
      usePortal
      dataTestId="publish-modal">
      {isLoading ? (
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      ) : (
        <Wizard
          keepDirtyOnReinitialize
          initialValues={filteredInitialValues}
          onSubmit={handleSubmit}
          doneText="send"
          showDotsNavigation={!isMobileOnly}>
          <Wizard.Page>
            <>
              <Field
                name="items"
                render={({ input }) =>
                  filteredItems.length === 1 ? (
                    <PublishOneItem
                      params={params}
                      isShift={isShift}
                      {...input}
                      generateDisabledFilters={generateDisabledFilters}
                      branches={branches}
                    />
                  ) : (
                    <Container>
                      <PublishEmployees
                        isShift={isShift}
                        items={filteredItems}
                        params={params}
                        search={search}
                        setSearch={setSearch}
                        {...input}
                        generateDisabledFilters={generateDisabledFilters}
                        branches={branches}
                      />
                    </Container>
                  )
                }
              />
              {showPromptPublishNotSeen && (
                <ConfirmationModal
                  onClose={() => setShowPromptPublishNotSeen(false)}
                  title={t('publishToNotSeenCommand')}
                  body={t('wouldYouLikeToPublishToNotSeen')}
                  onConfirm={() => {
                    setNotificationConditions?.();
                    onClose();
                  }}
                  onCancelClick={onClose}
                  confirmText={t('yes')}
                  cancelText={t('no')}
                  showCancelButton
                />
              )}
            </>
          </Wizard.Page>
          {addMode && (
            <Wizard.Page>
              <Container>
                <PublishSettings isCheckAvailabilityNeeded={isCheckAvailabilityNeeded} />
              </Container>
            </Wizard.Page>
          )}
        </Wizard>
      )}
    </Modal>
  );
};

export default React.memo(PublishModal);
